import { useEffect, useRef } from 'react';
import { recordModuleTime, recordModuleTimeBeacon } from '../../api/me';

/**
 * Track wallclock dwell time on the current course module and POST it when the
 * learner moves to another module or hides/closes the tab — so time spent is
 * captured even when the module is never explicitly completed (the original
 * gap that left AHT/active-time at zero).
 *
 * Semantics:
 * - **Wallclock** — idle and backgrounded time count; a single window is clamped
 *   client-side to the per-call cap (`MODULE_TIME_CAP_SECONDS`) before sending,
 *   so a long idle window records the capped value instead of being rejected.
 * - **In-app navigation (reliable):** every module change unmounts the player
 *   page, so the effect cleanup fires and flushes the dwell via an in-session
 *   request.
 * - **Hard unload (best-effort):** `pagehide` / `visibilitychange(hidden)` flush
 *   via `navigator.sendBeacon`. The flush resets the clock so the trailing
 *   cleanup can't double-count the same span.
 *
 * Pass `undefined` for either id to disable (e.g. while a module resolves, or
 * for module types handled by a different player page).
 */
export interface ModuleTimerHandle {
  /** Seconds since the module opened (or since the last take/flush) — and
   *  resets the clock, so the same span is never reported twice. */
  takeElapsed: () => number;
}

export function useModuleTimer(
  assignmentId: string | undefined,
  moduleId: string | undefined
): ModuleTimerHandle {
  const openedAtRef = useRef<number>(Date.now());

  useEffect(() => {
    openedAtRef.current = Date.now();
    if (!assignmentId || !moduleId) return undefined;

    const elapsedSeconds = () =>
      Math.max(0, Math.round((Date.now() - openedAtRef.current) / 1000));

    const flushBeacon = () => {
      const seconds = elapsedSeconds();
      if (seconds > 0) {
        recordModuleTimeBeacon(assignmentId, moduleId, seconds);
        // Reset so the trailing cleanup doesn't re-send the same span.
        openedAtRef.current = Date.now();
      }
    };
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') flushBeacon();
    };

    window.addEventListener('pagehide', flushBeacon);
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      window.removeEventListener('pagehide', flushBeacon);
      document.removeEventListener('visibilitychange', onVisibility);
      const seconds = elapsedSeconds();
      if (seconds > 0) {
        // Fire-and-forget — navigation is already underway.
        void recordModuleTime(assignmentId, moduleId, seconds).catch(() => {});
      }
    };
  }, [assignmentId, moduleId]);

  return {
    takeElapsed: () => {
      const seconds = Math.max(0, Math.round((Date.now() - openedAtRef.current) / 1000));
      openedAtRef.current = Date.now();
      return seconds;
    },
  };
}
